import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { authApi } from '@/services/api/auth'
import { ApiError, onUnauthorized } from '@/services/api/client'
import { tokenStore } from '@/services/api/token'
import type {
  AuthSessionResponse,
  ClientUser,
  LoginRequest,
  RegisterRequest,
  TradeMode,
} from '@/services/api/types'
import { wsManager } from '@/services/websocket'

interface AuthContextValue {
  isAuthenticated: boolean
  accessToken: string | null
  client: ClientUser | null
  tradeMode: TradeMode | null
  /** `true` while bootstrapping `/client/auth/me` with a stored token. */
  bootstrapping: boolean
  canTrade: boolean
  applySession: (session: AuthSessionResponse) => void
  login: (body: LoginRequest) => Promise<AuthSessionResponse>
  register: (body: RegisterRequest) => Promise<AuthSessionResponse>
  refreshMe: () => Promise<void>
  /** Calls API logout when possible, then clears local session. */
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    tokenStore.getAccessToken(),
  )
  const [client, setClient] = useState<ClientUser | null>(() =>
    tokenStore.getClient(),
  )
  const [tradeMode, setTradeMode] = useState<TradeMode | null>(() =>
    tokenStore.getTradeMode(),
  )
  const [bootstrapping, setBootstrapping] = useState(
    () => Boolean(tokenStore.getAccessToken()),
  )

  const clearLocal = useCallback(() => {
    tokenStore.clear()
    setAccessToken(null)
    setClient(null)
    setTradeMode(null)
    wsManager.reset()
  }, [])

  const applySession = useCallback((session: AuthSessionResponse) => {
    tokenStore.setSession(session.token, {
      tradeMode: session.trade_mode,
      client: session.client,
    })
    setAccessToken(session.token)
    setClient(session.client)
    setTradeMode(session.trade_mode)
    wsManager.connect()
  }, [])

  const login = useCallback(
    async (body: LoginRequest) => {
      const session = await authApi.login(body)
      applySession(session)
      return session
    },
    [applySession],
  )

  const register = useCallback(
    async (body: RegisterRequest) => {
      const session = await authApi.register(body)
      applySession(session)
      return session
    },
    [applySession],
  )

  const refreshMe = useCallback(async () => {
    const res = await authApi.me()
    setClient(res.data)
    setTradeMode(res.trade_mode)
    const token = tokenStore.getAccessToken()
    if (token) {
      tokenStore.setSession(token, {
        tradeMode: res.trade_mode,
        client: res.data,
      })
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      if (tokenStore.getAccessToken()) {
        await authApi.logout()
      }
    } catch (err) {
      // 401 after revoke is fine — still clear locally
      if (!(err instanceof ApiError && err.status === 401)) {
        console.warn('[auth] logout request failed', err)
      }
    } finally {
      clearLocal()
    }
  }, [clearLocal])

  useEffect(() => onUnauthorized(() => clearLocal()), [clearLocal])

  useEffect(() => {
    if (!accessToken) {
      setBootstrapping(false)
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const res = await authApi.me()
        if (cancelled) return
        setClient(res.data)
        setTradeMode(res.trade_mode)
        tokenStore.setSession(accessToken, {
          tradeMode: res.trade_mode,
          client: res.data,
        })
        wsManager.connect()
      } catch {
        if (!cancelled) clearLocal()
      } finally {
        if (!cancelled) setBootstrapping(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [accessToken, clearLocal])

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(accessToken),
      accessToken,
      client,
      tradeMode,
      bootstrapping,
      canTrade: tradeMode === 'full',
      applySession,
      login,
      register,
      refreshMe,
      logout,
    }),
    [
      accessToken,
      client,
      tradeMode,
      bootstrapping,
      applySession,
      login,
      register,
      refreshMe,
      logout,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
